import type { CmmdCloudProjectLifecyclePreflight } from "@cmmd-forge/contracts";

type JsonObject = Readonly<Record<string, unknown>>;

const runtimeStatuses = new Set([
  "not_requested",
  "machine_creating",
  "machine_starting",
  "supervisor_up",
  "server_ready",
  "error",
]);
const removalStatuses = new Set(["pending", "running", "retryable"]);
const removalPhases = new Set([
  "destroy_runtime",
  "purge_execution_state",
  "delete_planning_project",
  "unregister",
  "complete",
]);
export const exactForgeServerVersionPattern =
  /^(?:0|[1-9][0-9]*)[.](?:0|[1-9][0-9]*)[.](?:0|[1-9][0-9]*)(?:-(?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*)(?:[.](?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*))*)?(?:[+][0-9A-Za-z-]+(?:[.][0-9A-Za-z-]+)*)?$/;

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: JsonObject, keys: ReadonlyArray<string>): boolean {
  const allowed = new Set(keys);
  return Object.keys(value).every((key) => allowed.has(key));
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isForgeServerVersionOrNull(value: unknown): value is string | null {
  return (
    value === null || (typeof value === "string" && exactForgeServerVersionPattern.test(value))
  );
}

function isDateTime(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function isDateTimeOrNull(value: unknown): value is string | null {
  return value === null || isDateTime(value);
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && typeof value === "number" && value >= 0;
}

function isSupervisor(value: unknown): boolean {
  if (value === null) return true;
  if (!isObject(value)) return false;
  return (
    hasOnlyKeys(value, ["name", "status", "machineId", "lastHeartbeatAt"]) &&
    value["name"] === "forge" &&
    typeof value["status"] === "string" &&
    runtimeStatuses.has(value["status"]) &&
    typeof value["machineId"] === "string" &&
    isDateTimeOrNull(value["lastHeartbeatAt"])
  );
}

function hasRuntimeIdentity(value: JsonObject): boolean {
  return (
    typeof value["runtimeId"] === "string" &&
    isStringOrNull(value["machineId"]) &&
    isNonNegativeSafeInteger(value["credentialEpoch"]) &&
    isStringOrNull(value["forgeGitCommit"])
  );
}

function hasRuntimeLifecycle(value: JsonObject): boolean {
  return (
    typeof value["status"] === "string" &&
    typeof value["readinessPhase"] === "string" &&
    typeof value["desiredState"] === "string" &&
    (value["substrate"] === "sprites" || value["substrate"] === "fly")
  );
}

function hasRuntimeEvidence(value: JsonObject): boolean {
  return (
    isSupervisor(value["supervisedService"]) &&
    isDateTimeOrNull(value["lastActivityAt"]) &&
    isDateTimeOrNull(value["lastConnectedAt"])
  );
}

function isRuntime(value: unknown): boolean {
  if (value === null) return true;
  if (!isObject(value)) return false;
  return (
    hasOnlyKeys(value, [
      "runtimeId",
      "status",
      "readinessPhase",
      "desiredState",
      "substrate",
      "machineId",
      "credentialEpoch",
      "forgeGitCommit",
      "forgeServerVersion",
      "supervisedService",
      "lastActivityAt",
      "lastConnectedAt",
    ]) &&
    hasRuntimeIdentity(value) &&
    isForgeServerVersionOrNull(value["forgeServerVersion"]) &&
    hasRuntimeLifecycle(value) &&
    hasRuntimeEvidence(value)
  );
}

function isInventory(value: unknown): boolean {
  if (value === null) return true;
  if (!isObject(value)) return false;
  return (
    hasOnlyKeys(value, ["projectCount", "threadCount", "providerCatalog", "publishedAt"]) &&
    isNonNegativeSafeInteger(value["projectCount"]) &&
    isNonNegativeSafeInteger(value["threadCount"]) &&
    (value["providerCatalog"] === "published" || value["providerCatalog"] === "unknown") &&
    isDateTime(value["publishedAt"])
  );
}

function isRemoval(value: unknown): boolean {
  if (value === null) return true;
  if (!isObject(value)) return false;
  return (
    hasOnlyKeys(value, ["operationId", "status", "phase", "deletePlanningProject", "updatedAt"]) &&
    isRemovalIdentity(value) &&
    isRemovalState(value) &&
    typeof value["deletePlanningProject"] === "boolean" &&
    isDateTime(value["updatedAt"])
  );
}

function isRemovalIdentity(value: JsonObject): boolean {
  return typeof value["operationId"] === "string";
}

function isRemovalState(value: JsonObject): boolean {
  return (
    typeof value["status"] === "string" &&
    removalStatuses.has(value["status"]) &&
    typeof value["phase"] === "string" &&
    removalPhases.has(value["phase"])
  );
}

export function parseCloudProjectLifecyclePreflight(
  value: unknown,
  expectedProjectId: number,
): CmmdCloudProjectLifecyclePreflight {
  if (!isObject(value)) throw new Error("CMMD returned an invalid lifecycle preflight receipt.");
  const registration = value["registration"];
  const valid =
    hasOnlyKeys(value, [
      "organizationId",
      "projectId",
      "registration",
      "runtime",
      "inventory",
      "removal",
      "observedAt",
    ]) &&
    isNonNegativeSafeInteger(value["organizationId"]) &&
    value["projectId"] === expectedProjectId &&
    isObject(registration) &&
    hasOnlyKeys(registration, ["source", "registeredAt"]) &&
    typeof registration["source"] === "string" &&
    isDateTime(registration["registeredAt"]) &&
    isRuntime(value["runtime"]) &&
    isInventory(value["inventory"]) &&
    isRemoval(value["removal"]) &&
    isDateTime(value["observedAt"]);
  if (!valid) throw new Error("CMMD returned an invalid lifecycle preflight receipt.");
  return value as CmmdCloudProjectLifecyclePreflight;
}
